import { Sidebar } from "@excalidraw/excalidraw";
import { useEffect } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { useDrawolaT } from "../../storage/i18n";
import { useShapeLibraryPreference } from "../useShapeLibraryPreference";

import { insertShapeAtClientPoint, isPointOverCanvas } from "./insertShape";
import { SHAPES_BY_CATEGORY } from "./shapes";

import "./DrawolaShapeLibrary.scss";

import type { ShapeDefinition } from "./shapes";

export const DRAWOLA_SHAPE_LIBRARY_SIDEBAR = "drawola-shapes";

type Props = {
  api: ExcalidrawImperativeAPI;
};

const ShapeTile = ({
  shape,
  api,
}: {
  shape: ShapeDefinition;
  api: ExcalidrawImperativeAPI;
}) => {
  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    // Custom MIME type so Excalidraw's own drop handlers ignore this drag.
    event.dataTransfer.setData("application/x-drawola-shape", shape.id);
    event.dataTransfer.effectAllowed = "copy";
  };

  const handleDragEnd = (event: React.DragEvent<HTMLDivElement>) => {
    const { clientX, clientY } = event;
    // dragend with (0, 0) typically means the drop was cancelled (ESC).
    if (clientX === 0 && clientY === 0) {
      return;
    }
    if (isPointOverCanvas(api, clientX, clientY)) {
      insertShapeAtClientPoint(api, shape, clientX, clientY);
    }
  };

  return (
    <div
      className="drawola-shape-library__shape"
      title={shape.name}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {shape.thumbnail}
    </div>
  );
};

export const DrawolaShapeLibrary = ({ api }: Props) => {
  const t = useDrawolaT();
  const [enabled, setEnabled] = useShapeLibraryPreference();

  // Open sidebar programmatically when preference flips on (or on mount).
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const current = api.getAppState().openSidebar;
    if (current?.name !== DRAWOLA_SHAPE_LIBRARY_SIDEBAR) {
      api.updateScene({
        appState: {
          openSidebar: { name: DRAWOLA_SHAPE_LIBRARY_SIDEBAR },
        },
      });
    }
  }, [enabled, api]);

  if (!enabled) {
    return null;
  }

  return (
    <Sidebar
      name={DRAWOLA_SHAPE_LIBRARY_SIDEBAR}
      docked
      onStateChange={(state) => {
        // User clicked the X close button → state becomes null.
        // Sync preference so the menu checkbox reflects reality.
        if (state === null) {
          setEnabled(false);
        }
      }}
      className="drawola-shape-library"
    >
      <Sidebar.Header>{t("shapeLibrary.title")}</Sidebar.Header>
      <div className="drawola-shape-library__body">
        <details className="drawola-shape-library__category" open>
          <summary className="drawola-shape-library__category-header">
            {t("shapeLibrary.categoryFlowchart")}
          </summary>
          <div className="drawola-shape-library__grid">
            {SHAPES_BY_CATEGORY.flowchart.map((shape) => (
              <ShapeTile key={shape.id} shape={shape} api={api} />
            ))}
          </div>
        </details>
      </div>
    </Sidebar>
  );
};

DrawolaShapeLibrary.displayName = "DrawolaShapeLibrary";
